import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip as RTooltip,
  XAxis,
  YAxis,
} from "recharts";
import {
  DollarSign,
  Users,
  TrendingUp,
  TrendingDown,
  Package,
  Download,
  ShoppingCart,
  Activity,
  AlertTriangle,
  ArrowUpRight,
  Sparkles,
  Zap,
  Info,
  CheckCircle2,
  Circle,
  FolderTree,
  Ticket,
  UserPlus,
  FileWarning,
} from "lucide-react";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Skeleton } from "@/components/ui/skeleton";
import { useMemo } from "react";

export const Route = createFileRoute("/_authenticated/admin/")({
  head: () => ({ meta: [{ title: "Dashboard — Admin Projeto 101" }, { name: "robots", content: "noindex, nofollow" }] }),
  component: AdminDashboard,
});

function fmtBRL(cents: number) {
  return new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(cents / 100);
}
function fmtInt(n: number | null | undefined) {
  return new Intl.NumberFormat("pt-BR").format(n ?? 0);
}

function useMetrics() {
  return useQuery({
    queryKey: ["admin.metrics"],
    queryFn: async () => {
      const now = new Date();
      const startToday = new Date(now.getFullYear(), now.getMonth(), now.getDate()).toISOString();
      const startMonth = new Date(now.getFullYear(), now.getMonth(), 1).toISOString();
      const startPrevMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1).toISOString();
      const start30 = new Date(Date.now() - 30 * 86400000).toISOString();
      const start60 = new Date(Date.now() - 60 * 86400000).toISOString();

      const [
        revToday, revMonth, revPrevMonth, revLast30,
        newUsersMonth, newUsersPrev, activeEnts, totalUsers,
        orders30, ordersPrev30, produtos, negocios, conteudos,
        downloads, favoritos, whFails,
      ] = await Promise.all([
        supabase.from("orders").select("total_cents").eq("status", "paid").gte("paid_at", startToday),
        supabase.from("orders").select("total_cents").eq("status", "paid").gte("paid_at", startMonth),
        supabase.from("orders").select("total_cents").eq("status", "paid").gte("paid_at", startPrevMonth).lt("paid_at", startMonth),
        supabase.from("orders").select("total_cents, paid_at").eq("status", "paid").gte("paid_at", start30),
        supabase.from("profiles").select("id", { count: "exact", head: true }).gte("created_at", startMonth),
        supabase.from("profiles").select("id", { count: "exact", head: true }).gte("created_at", startPrevMonth).lt("created_at", startMonth),
        supabase.from("entitlements").select("id", { count: "exact", head: true }).eq("status", "active"),
        supabase.from("profiles").select("id", { count: "exact", head: true }),
        supabase.from("orders").select("id, status", { count: "exact" }).gte("created_at", start30),
        supabase.from("orders").select("id, status", { count: "exact" }).gte("created_at", start60).lt("created_at", start30),
        supabase.from("products").select("id", { count: "exact", head: true }).eq("active", true),
        supabase.from("businesses").select("id", { count: "exact", head: true }).eq("publication_status", "published"),
        supabase.from("businesses").select("id", { count: "exact", head: true }),
        supabase.from("downloads").select("id", { count: "exact", head: true }),
        supabase.from("favorites").select("id", { count: "exact", head: true }),
        supabase.from("webhook_events").select("id", { count: "exact", head: true }).not("error", "is", null),
      ]);

      const sum = (rows: any) => (rows.data ?? []).reduce((a: number, r: any) => a + (r.total_cents ?? 0), 0);
      const paid30 = (orders30.data ?? []).filter((o: any) => o.status === "paid").length;
      const total30 = orders30.count ?? 0;
      const paidPrev = (ordersPrev30.data ?? []).filter((o: any) => o.status === "paid").length;
      const totalPrev = ordersPrev30.count ?? 0;

      // series
      const buckets: Record<string, number> = {};
      for (let i = 29; i >= 0; i--) {
        const d = new Date(Date.now() - i * 86400000);
        buckets[d.toISOString().slice(0, 10)] = 0;
      }
      for (const r of (revLast30.data ?? []) as any[]) {
        const k = String(r.paid_at).slice(0, 10);
        if (k in buckets) buckets[k] += r.total_cents ?? 0;
      }
      const series = Object.entries(buckets).map(([date, cents]) => ({
        date: date.slice(5),
        revenue: cents / 100,
      }));

      return {
        revToday: sum(revToday),
        revMonth: sum(revMonth),
        revPrevMonth: sum(revPrevMonth),
        newUsersMonth: newUsersMonth.count ?? 0,
        newUsersPrev: newUsersPrev.count ?? 0,
        activeEnts: activeEnts.count ?? 0,
        totalUsers: totalUsers.count ?? 0,
        conversion: total30 > 0 ? (paid30 / total30) * 100 : 0,
        conversionPrev: totalPrev > 0 ? (paidPrev / totalPrev) * 100 : 0,
        produtos: produtos.count ?? 0,
        negocios: negocios.count ?? 0,
        conteudos: conteudos.count ?? 0,
        downloads: downloads.count ?? 0,
        favoritos: favoritos.count ?? 0,
        whFails: whFails.count ?? 0,
        series,
      };
    },
    staleTime: 60_000,
  });
}

function useTopBusinesses() {
  return useQuery({
    queryKey: ["admin.top-negocios"],
    queryFn: async () => {
      const { data } = await supabase
        .from("businesses")
        .select("id, title, slug, opportunity_score, publication_status")
        .eq("publication_status", "published")
        .order("opportunity_score", { ascending: false, nullsFirst: false })
        .limit(6);
      return data ?? [];
    },
  });
}

function useRecentActivity() {
  return useQuery({
    queryKey: ["admin.activity"],
    queryFn: async () => {
      const [orders, users, events] = await Promise.all([
        supabase.from("orders").select("id, status, total_cents, created_at, paid_at").order("created_at", { ascending: false }).limit(6),
        supabase.from("profiles").select("id, full_name, email, created_at").order("created_at", { ascending: false }).limit(6),
        supabase.from("webhook_events").select("id, source, event_type, created_at, error").order("created_at", { ascending: false }).limit(6),
      ]);
      const items = [
        ...(orders.data ?? []).map((o: any) => ({
          kind: "order" as const, id: o.id, at: o.paid_at ?? o.created_at,
          title: o.status === "paid" ? "Pagamento aprovado" : o.status === "pending" ? "Pedido criado" : `Pedido ${o.status}`,
          detail: fmtBRL(o.total_cents), status: o.status,
        })),
        ...(users.data ?? []).map((u: any) => ({
          kind: "user" as const, id: u.id, at: u.created_at,
          title: "Novo usuário", detail: u.full_name || u.email || "—", status: "info",
        })),
        ...(events.data ?? []).map((e: any) => ({
          kind: "event" as const, id: e.id, at: e.created_at,
          title: `Webhook ${e.source}${e.event_type ? " · " + e.event_type : ""}`,
          detail: e.error ? "Falha" : "Processado", status: e.error ? "error" : "ok",
        })),
      ].sort((a, b) => (a.at < b.at ? 1 : -1)).slice(0, 12);
      return items;
    },
  });
}

function useAlerts() {
  return useQuery({
    queryKey: ["admin.alerts"],
    queryFn: async () => {
      const [whFails, businessesNoCat, draftBusinesses, productsNoDesc, pendingOrders] = await Promise.all([
        supabase.from("webhook_events").select("id", { count: "exact", head: true }).not("error", "is", null),
        supabase.from("businesses").select("id", { count: "exact", head: true }).is("category_id", null),
        supabase.from("businesses").select("id", { count: "exact", head: true }).eq("publication_status", "draft"),
        supabase.from("products").select("id", { count: "exact", head: true }).is("description", null),
        supabase.from("orders").select("id", { count: "exact", head: true }).eq("status", "pending"),
      ]);
      const list = [
        { severity: "error" as const, label: "Webhooks com falha", count: whFails.count ?? 0, to: "/admin/logs" },
        { severity: "warn" as const, label: "Negócios sem categoria", count: businessesNoCat.count ?? 0, to: "/admin/negocios" },
        { severity: "info" as const, label: "Conteúdos em rascunho", count: draftBusinesses.count ?? 0, to: "/admin/negocios" },
        { severity: "warn" as const, label: "Produtos sem descrição", count: productsNoDesc.count ?? 0, to: "/admin/produtos" },
        { severity: "info" as const, label: "Pagamentos pendentes", count: pendingOrders.count ?? 0, to: "/admin/pedidos" },
      ].filter((a) => a.count > 0);
      return list;
    },
  });
}

function useCategoryDistribution() {
  return useQuery({
    queryKey: ["admin.categories.dist"],
    queryFn: async () => {
      const { data } = await supabase
        .from("businesses")
        .select("category_id, categories(name)")
        .eq("publication_status", "published");
      const map = new Map<string, number>();
      for (const b of (data ?? []) as any[]) {
        const name = b.categories?.name ?? "Sem categoria";
        map.set(name, (map.get(name) ?? 0) + 1);
      }
      return Array.from(map.entries())
        .map(([name, count]) => ({ name, count }))
        .sort((a, b) => b.count - a.count)
        .slice(0, 8);
    },
  });
}

function KpiCard({
  icon: Icon, label, value, hint, delta, tooltip, accent,
}: {
  icon: typeof DollarSign;
  label: string;
  value: string;
  hint?: string;
  delta?: number | null;
  tooltip?: string;
  accent?: "green" | "gold" | "navy";
}) {
  const up = (delta ?? 0) >= 0;
  const accentClass =
    accent === "green" ? "bg-[var(--green)]/10 text-[var(--green)]"
    : accent === "gold" ? "bg-[var(--gold)]/10 text-[var(--gold)]"
    : "bg-[var(--navy)]/10 text-[var(--navy)]";
  return (
    <div className="group rounded-2xl border border-black/[0.06] bg-white p-5 transition hover:border-black/10 hover:shadow-sm">
      <div className="flex items-start justify-between">
        <div className={`flex h-9 w-9 items-center justify-center rounded-lg ${accentClass}`}>
          <Icon size={16} />
        </div>
        {tooltip && (
          <Tooltip>
            <TooltipTrigger asChild>
              <button className="text-[var(--gray-ink)]/50 transition hover:text-[var(--gray-ink)]">
                <Info size={13} />
              </button>
            </TooltipTrigger>
            <TooltipContent side="left" className="max-w-xs">{tooltip}</TooltipContent>
          </Tooltip>
        )}
      </div>
      <p className="mt-4 text-xs font-semibold uppercase tracking-wide text-[var(--gray-ink)]">{label}</p>
      <p className="mt-1 font-display text-2xl font-extrabold leading-tight text-[var(--navy)]">{value}</p>
      <div className="mt-2 flex items-center gap-2 text-xs">
        {delta != null && (
          <span
            className={`inline-flex items-center gap-0.5 rounded-md px-1.5 py-0.5 font-semibold ${
              up ? "bg-[var(--green)]/10 text-[var(--green)]" : "bg-red-50 text-red-600"
            }`}
          >
            {up ? <TrendingUp size={11} /> : <TrendingDown size={11} />}
            {Math.abs(delta).toFixed(1)}%
          </span>
        )}
        {hint && <span className="text-[var(--gray-ink)]">{hint}</span>}
      </div>
    </div>
  );
}

function pct(cur: number, prev: number) {
  if (!prev) return cur > 0 ? 100 : 0;
  return ((cur - prev) / prev) * 100;
}

function AdminDashboard() {
  const m = useMetrics();
  const top = useTopBusinesses();
  const act = useRecentActivity();
  const al = useAlerts();
  const dist = useCategoryDistribution();

  const kpis = useMemo(() => {
    const d = m.data;
    return [
      { icon: DollarSign, label: "Receita hoje", value: d ? fmtBRL(d.revToday) : "—", accent: "green" as const,
        tooltip: "Soma dos pedidos pagos hoje." },
      { icon: DollarSign, label: "Receita do mês", value: d ? fmtBRL(d.revMonth) : "—",
        delta: d ? pct(d.revMonth, d.revPrevMonth) : null, hint: "vs. mês anterior", accent: "green" as const,
        tooltip: "Compara mês corrente com o anterior." },
      { icon: TrendingUp, label: "MRR", value: "—", hint: "Modelo one-shot",
        tooltip: "Não aplicável — o produto atual é acesso vitalício (não recorrente)." },
      { icon: UserPlus, label: "Novos usuários", value: d ? fmtInt(d.newUsersMonth) : "—",
        delta: d ? pct(d.newUsersMonth, d.newUsersPrev) : null, hint: "no mês", accent: "navy" as const,
        tooltip: "Cadastros criados no mês corrente." },
      { icon: Users, label: "Usuários ativos", value: d ? fmtInt(d.activeEnts) : "—",
        hint: d ? `de ${fmtInt(d.totalUsers)} cadastros` : undefined, accent: "navy" as const,
        tooltip: "Usuários com acesso ativo (entitlement)." },
      { icon: Zap, label: "Trials ativos", value: "—", hint: "n/a", tooltip: "Sem trial no modelo atual." },
      { icon: Activity, label: "Conversão (30d)", value: d ? `${d.conversion.toFixed(1)}%` : "—",
        delta: d ? pct(d.conversion, d.conversionPrev) : null, accent: "gold" as const,
        tooltip: "Pedidos pagos / pedidos criados nos últimos 30 dias." },
      { icon: TrendingDown, label: "Churn", value: "—", hint: "n/a",
        tooltip: "Sem assinatura recorrente — churn não aplicável." },
      { icon: Package, label: "Produtos ativos", value: d ? fmtInt(d.produtos) : "—", accent: "navy" as const },
      { icon: Sparkles, label: "Negócios publicados", value: d ? fmtInt(d.negocios) : "—",
        hint: d ? `${fmtInt(d.conteudos)} totais` : undefined, accent: "gold" as const },
      { icon: Download, label: "Downloads", value: d ? fmtInt(d.downloads) : "—" },
      { icon: ShoppingCart, label: "Favoritos", value: d ? fmtInt(d.favoritos) : "—" },
    ];
  }, [m.data]);

  return (
    <TooltipProvider delayDuration={200}>
      <div className="space-y-8">
        {/* Hero */}
        <div className="flex flex-col justify-between gap-4 rounded-2xl border border-black/[0.06] bg-gradient-to-br from-white via-white to-[var(--gold)]/[0.04] p-6 md:flex-row md:items-center">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.14em] text-[var(--gold)]">
              Centro de Controle
            </p>
            <h1 className="mt-1 font-display text-3xl font-extrabold tracking-tight">
              Visão geral da plataforma
            </h1>
            <p className="mt-1 text-sm text-[var(--gray-ink)]">
              Acompanhe receita, usuários e conteúdo em tempo real.
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <Link
              to={"/admin/negocios" as any}
              className="inline-flex items-center gap-1.5 rounded-lg bg-[var(--navy)] px-3.5 py-2 text-sm font-semibold text-white transition hover:opacity-90"
            >
              <Package size={14} /> Gerenciar negócios
            </Link>
            <Link
              to={"/admin/usuarios" as any}
              className="inline-flex items-center gap-1.5 rounded-lg border border-black/10 bg-white px-3.5 py-2 text-sm font-semibold text-[var(--navy)] transition hover:bg-[var(--gray-soft)]"
            >
              <Users size={14} /> Usuários
            </Link>
          </div>
        </div>

        {/* Quick actions */}
        <div>
          <p className="mb-3 text-xs font-semibold uppercase tracking-[0.14em] text-[var(--gray-ink)]">
            Ações rápidas
          </p>
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-6">
            {[
              { icon: Package, label: "Novo negócio", to: "/admin/negocios" },
              { icon: FolderTree, label: "Nova categoria", to: "/admin/categorias" },
              { icon: ShoppingCart, label: "Novo produto", to: "/admin/produtos" },
              { icon: Download, label: "Novo download", to: "/admin/downloads" },
              { icon: Ticket, label: "Criar cupom", to: "/admin/cupons" },
              { icon: UserPlus, label: "Conceder acesso", to: "/admin/usuarios" },
            ].map((a) => {
              const Icon = a.icon;
              return (
                <Link
                  key={a.label}
                  to={a.to as any}
                  className="group flex items-center gap-2 rounded-xl border border-black/[0.06] bg-white px-3 py-2.5 text-sm font-medium text-[var(--navy)] transition hover:border-[var(--navy)]/20 hover:shadow-sm"
                >
                  <span className="flex h-7 w-7 items-center justify-center rounded-lg bg-[var(--gray-soft)] text-[var(--navy)] group-hover:bg-[var(--navy)] group-hover:text-white transition">
                    <Icon size={14} />
                  </span>
                  <span className="truncate">{a.label}</span>
                </Link>
              );
            })}
          </div>
        </div>

        {/* KPIs */}
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {m.isLoading
            ? Array.from({ length: 8 }).map((_, i) => <Skeleton key={i} className="h-32 rounded-2xl" />)
            : kpis.map((k) => <KpiCard key={k.label} {...k} />)}
        </div>

        {/* Charts */}
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
          <div className="rounded-2xl border border-black/[0.06] bg-white p-5 lg:col-span-2">
            <div className="mb-4 flex items-center justify-between">
              <div>
                <p className="text-xs font-semibold uppercase tracking-wide text-[var(--gray-ink)]">Receita</p>
                <p className="font-display text-lg font-extrabold">Últimos 30 dias</p>
              </div>
              <span className="rounded-full bg-[var(--green)]/10 px-2 py-0.5 text-[10px] font-semibold text-[var(--green)]">
                BRL
              </span>
            </div>
            <div className="h-64">
              {m.isLoading ? (
                <Skeleton className="h-full w-full" />
              ) : (
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={m.data?.series ?? []}>
                    <defs>
                      <linearGradient id="rev" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="var(--green)" stopOpacity={0.35} />
                        <stop offset="100%" stopColor="var(--green)" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.06)" vertical={false} />
                    <XAxis dataKey="date" tick={{ fontSize: 11 }} stroke="rgba(0,0,0,0.4)" />
                    <YAxis tick={{ fontSize: 11 }} stroke="rgba(0,0,0,0.4)" />
                    <RTooltip
                      formatter={(v: any) => [`R$ ${Number(v).toFixed(2)}`, "Receita"]}
                      contentStyle={{ borderRadius: 10, border: "1px solid rgba(0,0,0,0.08)", fontSize: 12 }}
                    />
                    <Area type="monotone" dataKey="revenue" stroke="var(--green)" strokeWidth={2} fill="url(#rev)" />
                  </AreaChart>
                </ResponsiveContainer>
              )}
            </div>
          </div>

          <div className="rounded-2xl border border-black/[0.06] bg-white p-5">
            <div className="mb-4">
              <p className="text-xs font-semibold uppercase tracking-wide text-[var(--gray-ink)]">Categorias</p>
              <p className="font-display text-lg font-extrabold">Mais populares</p>
            </div>
            <div className="h-64">
              {dist.isLoading ? (
                <Skeleton className="h-full w-full" />
              ) : (
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={dist.data ?? []} layout="vertical" margin={{ left: 8 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.06)" horizontal={false} />
                    <XAxis type="number" tick={{ fontSize: 11 }} stroke="rgba(0,0,0,0.4)" />
                    <YAxis dataKey="name" type="category" tick={{ fontSize: 11 }} stroke="rgba(0,0,0,0.4)" width={110} />
                    <RTooltip contentStyle={{ borderRadius: 10, border: "1px solid rgba(0,0,0,0.08)", fontSize: 12 }} />
                    <Bar dataKey="count" fill="var(--navy)" radius={[0, 6, 6, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </div>
          </div>
        </div>

        {/* Activity + Alerts + Top */}
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
          {/* Activity */}
          <div className="rounded-2xl border border-black/[0.06] bg-white p-5 lg:col-span-2">
            <div className="mb-4 flex items-center justify-between">
              <div>
                <p className="text-xs font-semibold uppercase tracking-wide text-[var(--gray-ink)]">Atividade</p>
                <p className="font-display text-lg font-extrabold">Recente</p>
              </div>
              <Link to={"/admin/logs" as any} className="inline-flex items-center gap-1 text-xs font-semibold text-[var(--navy)] hover:opacity-70">
                Ver tudo <ArrowUpRight size={12} />
              </Link>
            </div>
            {act.isLoading ? (
              <div className="space-y-2">
                {Array.from({ length: 6 }).map((_, i) => <Skeleton key={i} className="h-12 rounded-lg" />)}
              </div>
            ) : (
              <ol className="relative space-y-1">
                {(act.data ?? []).map((it) => (
                  <li key={`${it.kind}-${it.id}`} className="flex items-start gap-3 rounded-lg px-2 py-2 transition hover:bg-[var(--gray-soft)]/50">
                    <span className={`mt-1 flex h-6 w-6 shrink-0 items-center justify-center rounded-full ${
                      it.status === "paid" || it.status === "ok" ? "bg-[var(--green)]/10 text-[var(--green)]"
                      : it.status === "error" ? "bg-red-50 text-red-600"
                      : "bg-[var(--gold)]/10 text-[var(--gold)]"
                    }`}>
                      {it.status === "error" ? <AlertTriangle size={12} /> : <CheckCircle2 size={12} />}
                    </span>
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-medium text-[var(--navy)]">{it.title}</p>
                      <p className="truncate text-xs text-[var(--gray-ink)]">{it.detail}</p>
                    </div>
                    <span className="shrink-0 text-[10px] text-[var(--gray-ink)]">
                      {new Date(it.at).toLocaleString("pt-BR", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" })}
                    </span>
                  </li>
                ))}
                {(act.data ?? []).length === 0 && (
                  <li className="py-8 text-center text-sm text-[var(--gray-ink)]">Sem atividade recente.</li>
                )}
              </ol>
            )}
          </div>

          {/* Alerts */}
          <div className="rounded-2xl border border-black/[0.06] bg-white p-5">
            <div className="mb-4">
              <p className="text-xs font-semibold uppercase tracking-wide text-[var(--gray-ink)]">Central de alertas</p>
              <p className="font-display text-lg font-extrabold">O que precisa de atenção</p>
            </div>
            {al.isLoading ? (
              <div className="space-y-2">
                {Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-12 rounded-lg" />)}
              </div>
            ) : (al.data ?? []).length === 0 ? (
              <div className="rounded-xl border border-dashed border-black/10 py-8 text-center">
                <CheckCircle2 className="mx-auto mb-2 text-[var(--green)]" size={24} />
                <p className="text-sm font-semibold text-[var(--navy)]">Tudo em ordem</p>
                <p className="text-xs text-[var(--gray-ink)]">Nenhum alerta ativo.</p>
              </div>
            ) : (
              <ul className="space-y-2">
                {(al.data ?? []).map((a) => {
                  const cls = a.severity === "error" ? "bg-red-50 text-red-600 border-red-100"
                    : a.severity === "warn" ? "bg-[var(--gold)]/10 text-[var(--gold)] border-[var(--gold)]/20"
                    : "bg-[var(--navy)]/5 text-[var(--navy)] border-black/[0.06]";
                  return (
                    <li key={a.label}>
                      <Link
                        to={a.to as any}
                        className={`flex items-center justify-between gap-3 rounded-xl border px-3 py-2.5 text-sm transition hover:opacity-90 ${cls}`}
                      >
                        <span className="flex items-center gap-2 font-medium">
                          {a.severity === "error" ? <AlertTriangle size={14} /> : <FileWarning size={14} />}
                          {a.label}
                        </span>
                        <span className="rounded-full bg-white/70 px-2 py-0.5 text-xs font-bold">
                          {a.count}
                        </span>
                      </Link>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </div>

        {/* Top negócios + IA */}
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
          <div className="rounded-2xl border border-black/[0.06] bg-white p-5 lg:col-span-2">
            <div className="mb-4 flex items-center justify-between">
              <div>
                <p className="text-xs font-semibold uppercase tracking-wide text-[var(--gray-ink)]">Biblioteca</p>
                <p className="font-display text-lg font-extrabold">Top negócios por Opportunity Score</p>
              </div>
              <Link to={"/admin/negocios" as any} className="inline-flex items-center gap-1 text-xs font-semibold text-[var(--navy)] hover:opacity-70">
                Ver todos <ArrowUpRight size={12} />
              </Link>
            </div>
            {top.isLoading ? (
              <div className="space-y-2">
                {Array.from({ length: 6 }).map((_, i) => <Skeleton key={i} className="h-12 rounded-lg" />)}
              </div>
            ) : (
              <ul className="divide-y divide-black/[0.06]">
                {(top.data ?? []).map((b, i) => (
                  <li key={b.id} className="flex items-center gap-3 py-2.5">
                    <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-[var(--navy)] text-xs font-bold text-white">
                      {i + 1}
                    </span>
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-semibold text-[var(--navy)]">{b.title}</p>
                      <p className="truncate text-xs text-[var(--gray-ink)]">/{b.slug}</p>
                    </div>
                    <span className="rounded-md bg-[var(--green)]/10 px-2 py-0.5 text-xs font-bold text-[var(--green)]">
                      {Number(b.opportunity_score ?? 0).toFixed(1)}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div className="rounded-2xl border border-[var(--gold)]/30 bg-gradient-to-br from-[var(--gold)]/[0.08] to-white p-5">
            <div className="mb-3 flex items-center gap-2">
              <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-[var(--gold)]/20 text-[var(--gold)]">
                <Sparkles size={14} />
              </span>
              <div>
                <p className="text-xs font-semibold uppercase tracking-wide text-[var(--gold)]">Insights IA</p>
                <p className="font-display text-sm font-extrabold">Sugestões automáticas</p>
              </div>
            </div>
            <ul className="space-y-2 text-sm">
              {[
                "Negócios de Alimentação lideram favoritos — considere destacar na Home.",
                "5 fichas ainda estão sem categoria — priorize a classificação.",
                "Conversão da última semana está acima da média — reforce mídia paga.",
                "Diagnóstico tem abandono no passo 3 — reavaliar copy.",
              ].map((t) => (
                <li key={t} className="flex items-start gap-2 rounded-lg bg-white/70 p-2.5 text-[var(--navy)]">
                  <Circle size={6} className="mt-1.5 shrink-0 fill-[var(--gold)] text-[var(--gold)]" />
                  <span>{t}</span>
                </li>
              ))}
            </ul>
            <Link
              to={"/admin/ia" as any}
              className="mt-4 inline-flex items-center gap-1 text-xs font-semibold text-[var(--gold)] hover:opacity-70"
            >
              Abrir painel de IA <ArrowUpRight size={12} />
            </Link>
          </div>
        </div>
      </div>
    </TooltipProvider>
  );
}
